/**
 * Lenavio Draft — AI Job Description generator modal.
 *
 * Two-step flow inside one modal:
 *
 *   1. **Inputs** — title + department + level + employment type +
 *      location + tone + key requirements (line-per-line). Submit
 *      button disabled until title is non-empty.
 *
 *   2. **Preview** — read-only markdown preview of Claude's output.
 *      "Edit" lets the admin tweak inline before "Use this draft"
 *      hands the final markdown back to the parent (typically the
 *      "New Job" form's description textarea).
 *
 * Stays entirely client-side after generation — the backend
 * doesn't persist the draft, the parent does (or doesn't, if the
 * admin closes the modal).
 */
import { useState } from "react";
import { Loader2, Sparkles } from "lucide-react";

import {
  generateJobDescription,
  type GenerateJDPayload,
  type JDTone,
} from "@/api/recruiting";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { useToast } from "@/hooks/useToast";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Initial title to seed the form with — saves a re-type when
      the admin opens the generator from inside an in-progress
      "New Job" form. */
  initialTitle?: string;
  /** Called with the chosen markdown when the admin clicks
      "Use this draft". The parent typically writes it into a
      textarea and closes the modal. */
  onUseDraft: (markdown: string) => void;
}

const TONE_OPTIONS: { value: JDTone; label: string }[] = [
  { value: "professional", label: "Professional" },
  { value: "casual", label: "Casual" },
  { value: "startup", label: "Startup" },
];

const LEVEL_HINTS = ["Junior", "Mid", "Senior", "Lead", "Staff", "Principal"];

export function JDGeneratorModal({
  isOpen,
  onClose,
  initialTitle,
  onUseDraft,
}: Props) {
  const { addToast } = useToast();
  const [title, setTitle] = useState(initialTitle || "");
  const [department, setDepartment] = useState("");
  const [level, setLevel] = useState("");
  const [employmentType, setEmploymentType] = useState("Full-time");
  const [location, setLocation] = useState("");
  const [salaryRange, setSalaryRange] = useState("");
  const [tone, setTone] = useState<JDTone>("professional");
  const [requirementsText, setRequirementsText] = useState("");

  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const handleGenerate = async () => {
    if (!title.trim()) {
      addToast("error", "Title is required.");
      return;
    }
    const reqs = requirementsText
      .split("\n")
      .map((r) => r.trim())
      .filter(Boolean);
    const payload: GenerateJDPayload = {
      title: title.trim(),
      department: department.trim() || undefined,
      level: level.trim() || undefined,
      employment_type: employmentType.trim() || undefined,
      location: location.trim() || undefined,
      key_requirements: reqs.length > 0 ? reqs : undefined,
      salary_range: salaryRange.trim() || undefined,
      tone,
    };
    setGenerating(true);
    try {
      const res = await generateJobDescription(payload);
      setDraft(res.description);
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Generation failed. Try again in a moment.";
      addToast("error", detail);
    } finally {
      setGenerating(false);
    }
  };

  const handleClose = () => {
    if (generating) return;
    // Reset state so reopening is clean.
    setDraft(null);
    setEditing(false);
    onClose();
  };

  const handleUse = () => {
    if (!draft) return;
    onUseDraft(draft);
    handleClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={draft ? "Review your draft" : "Generate a job description"}
      size="xl"
    >
      {!draft ? (
        // ── Step 1: inputs ──
        <div className="space-y-4">
          <p className="text-sm text-text-muted">
            Lenavio Draft writes a polished JD in seconds — fill in the
            essentials and we'll handle the rest.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              label="Title *"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Senior Product Manager"
              required
            />
            <Input
              label="Department"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              placeholder="Engineering"
            />
            <Input
              label="Level"
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              placeholder={`e.g. ${LEVEL_HINTS.join(", ")}`}
            />
            <Input
              label="Employment type"
              value={employmentType}
              onChange={(e) => setEmploymentType(e.target.value)}
              placeholder="Full-time / Contract / Part-time"
            />
            <Input
              label="Location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Remote / Lisbon / Hybrid NYC"
            />
            <Input
              label="Salary range"
              value={salaryRange}
              onChange={(e) => setSalaryRange(e.target.value)}
              placeholder="$120K–$150K"
            />
          </div>

          <div>
            <label
              htmlFor="jd-requirements"
              className="block text-sm font-medium text-text-primary mb-1"
            >
              Key requirements (one per line)
            </label>
            <textarea
              id="jd-requirements"
              value={requirementsText}
              onChange={(e) => setRequirementsText(e.target.value)}
              rows={4}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
              placeholder={
                "5+ years of B2B SaaS PM experience\nStrong written communication\nWorked with engineering teams"
              }
            />
            <p className="mt-1 text-xs text-text-muted">
              We'll polish these into the requirements section.
            </p>
          </div>

          <Select
            label="Tone"
            value={tone}
            onChange={(e) => setTone(e.target.value as JDTone)}
            options={TONE_OPTIONS}
          />

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={handleClose} disabled={generating}>
              Cancel
            </Button>
            <Button onClick={handleGenerate} isLoading={generating}>
              <Sparkles className="h-4 w-4" />
              {generating ? "Generating…" : "Generate"}
            </Button>
          </div>
        </div>
      ) : (
        // ── Step 2: preview / edit ──
        <div className="space-y-3">
          {editing ? (
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={20}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-accent/50"
            />
          ) : (
            <div className="rounded-lg border border-border bg-background p-4 max-h-[28rem] overflow-y-auto">
              <pre className="whitespace-pre-wrap text-sm text-text-primary font-sans">
                {draft}
              </pre>
            </div>
          )}

          <div className="flex justify-between gap-3 pt-2">
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => setEditing((v) => !v)}
              >
                {editing ? "Done editing" : "Edit"}
              </Button>
              <Button
                variant="ghost"
                onClick={handleGenerate}
                isLoading={generating}
                disabled={generating}
              >
                <Loader2 className="h-4 w-4" />
                Regenerate
              </Button>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={handleClose}>
                Discard
              </Button>
              <Button onClick={handleUse}>Use this draft</Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
